import { BACKEND_URL } from '../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Users, Trash2, Plus, Lock, AlertTriangle } from 'lucide-react';


const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [currentUser, setCurrentUser] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // 2FA Enforcement state (superadmin only)
  const [enforcementSettings, setEnforcementSettings] = useState(null);
  const [enforcementLoading, setEnforcementLoading] = useState(false);

  // New admin user state
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    email: '',
    password: '',
    role: 'admin'
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser?.role === 'superadmin') {
      if (activeTab === 'users') {
        fetchAdminUsers();
      }
      if (activeTab === 'enforcement') {
        fetchEnforcementSettings();
      }
    }
  }, [activeTab, currentUser]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/me`, getAuthHeader());
      setCurrentUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, getAuthHeader());
      setAdminUsers(response.data.admins);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      showMessage('error', 'Failed to load admin users');
    }
  };

  const fetchEnforcementSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings/2fa-enforcement`, getAuthHeader());
      setEnforcementSettings(response.data);
    } catch (error) {
      console.error('Error fetching 2FA enforcement settings:', error);
      showMessage('error', 'Failed to load 2FA enforcement settings');
    }
  };

  const toggleEnforcement = async (enable) => {
    if (enable && !window.confirm(
      `Are you sure you want to enable 2FA enforcement?\n\n` +
      `This will:\n` +
      `• Email all users without 2FA about the new requirement\n` +
      `• Give them ${enforcementSettings?.grace_period_days || 7} days to enable 2FA\n` +
      `• Automatically disable accounts that don't comply`
    )) {
      return;
    }
    
    setEnforcementLoading(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/settings/2fa-enforcement`,
        { 
          enforce_2fa: enable, 
          grace_period_days: enforcementSettings?.grace_period_days || 7 
        },
        getAuthHeader()
      );
      showMessage('success', response.data.message);
      fetchEnforcementSettings();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update 2FA enforcement');
    } finally {
      setEnforcementLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (newAdmin.password.length < 8) {
      showMessage('error', 'Password must be at least 8 characters');
      return;
    }
    
    if (!newAdmin.email) {
      showMessage('error', 'Email is required');
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users`,
        newAdmin,
        getAuthHeader()
      );
      
      showMessage('success', `Admin user '${newAdmin.username}' created successfully`);
      setNewAdmin({ username: '', email: '', password: '', role: 'admin' });
      fetchAdminUsers();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to create admin user');
    }
  };

  const handleDeleteAdmin = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete admin user '${username}'?`)) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/users/${userId}`,
        getAuthHeader()
      );
      
      showMessage('success', `Admin user '${username}' deleted successfully`);
      fetchAdminUsers();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to delete admin user');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto', backgroundColor: '#1a1f16', minHeight: '100vh' }}>
      <h1 style={{ fontSize: '2rem', marginBottom: '30px', color: '#c5bfa9' }}>
        <Shield style={{ display: 'inline', marginRight: '10px' }} />
        Admin Settings
      </h1>

      {/* Alert Messages */}
      {message.text && (
        <div style={{
          padding: '15px',
          marginBottom: '20px',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? '#10b981' : '#ef4444',
          color: '#fff'
        }}>
          {message.text}
        </div>
      )}

      {/* Access Restricted for Non-Superadmins */}
      {currentUser?.role !== 'superadmin' && (
        <div style={{
          padding: '40px',
          backgroundColor: '#1f2937',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <Shield size={48} style={{ color: '#6b7280', marginBottom: '20px' }} />
          <h2 style={{ fontSize: '1.5rem', marginBottom: '10px', color: '#fff' }}>Access Restricted</h2>
          <p style={{ color: '#9ca3af' }}>
            This settings page is for superadmins only.
            <br />
            To manage your account, password, or 2FA, please visit{' '}
            <a href="/admin/my-account" style={{ color: '#4a5d42' }}>My Account</a>.
          </p>
        </div>
      )}

      {/* Superadmin Content */}
      {currentUser?.role === 'superadmin' && (
        <>
          {/* Tabs */}
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            borderBottom: '2px solid #374151',
            marginBottom: '30px'
          }}>
            <button
              onClick={() => setActiveTab('users')}
              style={{
                padding: '12px 24px',
                backgroundColor: activeTab === 'users' ? '#4a5d42' : 'transparent',
                color: '#c5bfa9',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              <Users size={18} style={{ display: 'inline', marginRight: '8px' }} />
              Manage Admins
            </button>
            <button
              onClick={() => setActiveTab('enforcement')}
              style={{
                padding: '12px 24px',
                backgroundColor: activeTab === 'enforcement' ? '#4a5d42' : 'transparent',
                color: '#c5bfa9',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              <Lock size={18} style={{ display: 'inline', marginRight: '8px' }} />
              2FA Enforcement
            </button>
          </div>

          {/* Manage Admins Tab */}
          {activeTab === 'users' && (
            <div style={{ backgroundColor: '#1f2937', padding: '30px', borderRadius: '12px' }}>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '20px', color: '#fff' }}>Manage Admin Users</h2>
              
              {/* Create New Admin */}
              <div style={{ 
                backgroundColor: '#111827', 
                padding: '20px', 
                borderRadius: '8px',
                marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '15px', color: '#fff' }}>
                  <Plus size={20} style={{ display: 'inline', marginRight: '8px' }} />
                  Create New Admin
                </h3>
                <form onSubmit={handleCreateAdmin} style={{ display: 'grid', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#9ca3af' }}>Username</label>
                    <input
                      type="text"
                      value={newAdmin.username}
                      onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                      required
                      minLength="3"
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #374151',
                        backgroundColor: '#1f2937',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#9ca3af' }}>Email</label>
                    <input
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #374151',
                        backgroundColor: '#1f2937',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#9ca3af' }}>Password</label>
                    <input
                      type="password"
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                      required
                      minLength="8"
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #374151',
                        backgroundColor: '#1f2937',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                    <small style={{ color: '#9ca3af' }}>Minimum 8 characters. User will be forced to change on first login.</small>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#9ca3af' }}>Role</label>
                    <select
                      value={newAdmin.role}
                      onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #374151',
                        backgroundColor: '#1f2937',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="marshal">Marshal</option>
                      <option value="admin">Admin</option>
                      <option value="superadmin">Super Admin</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#10b981',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    <Plus size={18} style={{ display: 'inline', marginRight: '8px' }} />
                    Create Admin User
                  </button>
                </form>
              </div>

              {/* Admin Users List */}
              <h3 style={{ fontSize: '1.25rem', marginBottom: '15px', color: '#fff' }}>Current Admin Users</h3>
              <div style={{ display: 'grid', gap: '15px' }}>
                {adminUsers.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      backgroundColor: '#111827',
                      padding: '20px',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <h4 style={{ fontSize: '1.1rem', marginBottom: '5px', color: '#fff' }}>
                        {user.username}
                        {user.id === currentUser.id && (
                          <span style={{
                            marginLeft: '10px',
                            padding: '4px 8px',
                            backgroundColor: '#3b82f6',
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}>
                            You
                          </span>
                        )}
                      </h4>
                      <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                        {user.email} • Role: {user.role === 'superadmin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Marshal'}
                        {user.totp_enabled && ' • 2FA Enabled'}
                        {user.force_password_change && ' • Password Change Required'}
                        {!user.is_active && user.is_active !== undefined && ' • DISABLED'}
                      </p>
                      <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                        Created: {new Date(user.created_at).toLocaleDateString()}
                        {user.last_login && ` • Last login: ${new Date(user.last_login).toLocaleString()}`}
                      </p>
                    </div>
                    {user.id !== currentUser.id && (
                      <button
                        onClick={() => handleDeleteAdmin(user.id, user.username)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 2FA Enforcement Tab */}
          {activeTab === 'enforcement' && (
            <div style={{ backgroundColor: '#1f2937', padding: '30px', borderRadius: '12px' }}>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '20px', color: '#fff' }}>
                <Lock size={24} style={{ display: 'inline', marginRight: '10px' }} />
                2FA Enforcement
              </h2>
              
              <p style={{ color: '#9ca3af', marginBottom: '30px' }}>
                When enabled, all admin users will be required to set up two-factor authentication.
                Users who don't comply within the grace period will have their accounts automatically disabled.
              </p>

              {enforcementSettings && (
                <>
                  {/* Current Status */}
                  <div style={{
                    padding: '20px',
                    backgroundColor: enforcementSettings.enforce_2fa ? '#10b981' : '#374151',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <p style={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {enforcementSettings.enforce_2fa ? '✓ 2FA Enforcement is ENABLED' : '○ 2FA Enforcement is DISABLED'}
                    </p>
                    {enforcementSettings.enforce_2fa && enforcementSettings.enforce_2fa_enabled_at && (
                      <p style={{ color: 'rgba(255,255,255,0.8)', marginTop: '5px' }}>
                        Enabled on: {new Date(enforcementSettings.enforce_2fa_enabled_at).toLocaleString()}
                      </p>
                    )}
                  </div>

                  {/* Stats */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '15px',
                    marginBottom: '30px'
                  }}>
                    <div style={{ backgroundColor: '#111827', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                        {enforcementSettings.total_users - enforcementSettings.users_without_2fa}
                      </p>
                      <p style={{ color: '#9ca3af' }}>Users with 2FA</p>
                    </div>
                    <div style={{ backgroundColor: '#111827', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', color: enforcementSettings.users_without_2fa > 0 ? '#f59e0b' : '#10b981' }}>
                        {enforcementSettings.users_without_2fa}
                      </p>
                      <p style={{ color: '#9ca3af' }}>Users without 2FA</p>
                    </div>
                    <div style={{ backgroundColor: '#111827', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#fff' }}>
                        {enforcementSettings.total_users}
                      </p>
                      <p style={{ color: '#9ca3af' }}>Total Users</p>
                    </div>
                  </div>

                  {/* Grace Period Setting */}
                  {!enforcementSettings.enforce_2fa && (
                    <div style={{ marginBottom: '30px' }}>
                      <label style={{ display: 'block', marginBottom: '10px', color: '#9ca3af' }}>
                        Grace Period (days)
                      </label>
                      <select
                        value={enforcementSettings.grace_period_days}
                        onChange={(e) => setEnforcementSettings({
                          ...enforcementSettings,
                          grace_period_days: parseInt(e.target.value)
                        })}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #374151',
                          backgroundColor: '#111827',
                          color: '#fff',
                          fontSize: '1rem',
                          width: '150px'
                        }}
                      >
                        <option value="3">3 days</option>
                        <option value="7">7 days</option>
                        <option value="14">14 days</option>
                        <option value="30">30 days</option>
                      </select>
                      <p style={{ color: '#6b7280', marginTop: '5px', fontSize: '0.9rem' }}>
                        Users will have this many days to enable 2FA after enforcement is enabled.
                      </p>
                    </div>
                  )}

                  {/* Warning */}
                  {!enforcementSettings.enforce_2fa && enforcementSettings.users_without_2fa > 0 && (
                    <div style={{
                      padding: '15px',
                      backgroundColor: '#78350f',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <AlertTriangle size={20} color="#fbbf24" />
                      <p style={{ color: '#fef3c7' }}>
                        <strong>{enforcementSettings.users_without_2fa} user(s)</strong> don't have 2FA enabled. 
                        They will receive an email notification when you enable enforcement.
                      </p>
                    </div>
                  )}

                  {/* Toggle Button */}
                  <button
                    onClick={() => toggleEnforcement(!enforcementSettings.enforce_2fa)}
                    disabled={enforcementLoading}
                    style={{
                      padding: '14px 28px',
                      backgroundColor: enforcementSettings.enforce_2fa ? '#ef4444' : '#10b981',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      cursor: enforcementLoading ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      opacity: enforcementLoading ? 0.7 : 1
                    }}
                  >
                    {enforcementLoading ? 'Processing...' : (
                      enforcementSettings.enforce_2fa ? 'Disable 2FA Enforcement' : 'Enable 2FA Enforcement'
                    )}
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminSettings;
